import React, { PropsWithChildren } from 'react';

import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';

import { ListItem } from 'app/area/shared/list-components/list-item';
import { ListItemBody } from 'app/area/shared/list-components/list-item-body';
import { ListItemHeader } from 'app/area/shared/list-components/list-item-header';

import { processSavedReport } from '../../utils/process-saved-report';
import { SavedReportsTranslation } from '../../constants';
import { GenericSavedReport, SavedReportsValidation, SavedReportValidation } from '../../types-store';
import { OnUpdateSavedReport, SavedReportInfoRenderer, SavedReportFormRenderer, UpdateSavedReportHandler,
  ValidateSavedReportHandler } from '../../types';
import { SavedReportDragItemType } from '../constants';
import { SavedReportTileConfig } from '../types';

import { SavedReportDrag } from './saved-report-drag';
import { SavedReportDrop } from './saved-report-drop';
import { SavedReportDetails } from './saved-report-details';
import { SavedReportDetailsButton } from './saved-report-details-button';
import { SavedReportMeatball } from './saved-report-meatball';
import { SavedReportTileHeader } from './saved-report-tile-header';

import styles from './saved-report-tile.scss';

interface SavedReportTileProps<SavedReport extends GenericSavedReport> {
  savedReport: SavedReport;
  currentSavedReportId: number;
  currentSavedReport: SavedReport;
  currentValidation: SavedReportValidation<SavedReport>;
  allSavedReportIs: number[];

  prevElementSequence: number;
  nextElementSequence: number;

  renderSavedReportInfo: SavedReportInfoRenderer<SavedReport>;
  renderSavedReportForm: SavedReportFormRenderer<SavedReport>;

  onAddReports: (sequence?: number) => void;
  onAddById: (sequence?: number) => void;

  selectCurrent: (savedReport: SavedReport, onlyIdMode: boolean) => void;
  updateValidation: (validation: SavedReportValidation<SavedReport>) => void;

  loadEvaluation: (favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReport>) => void;
  displayError: (errorMessage: string) => void;

  validateSavedReport: ValidateSavedReportHandler<SavedReport>;

  updateSavedReport: UpdateSavedReportHandler<SavedReport>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  validation: SavedReportsValidation<SavedReport>;
  translation: SavedReportsTranslation;
  tileConfig: SavedReportTileConfig;
}

export const SavedReportTile = <SavedReport extends GenericSavedReport>({
  savedReport, currentSavedReportId, currentSavedReport, currentValidation, allSavedReportIs,
  prevElementSequence, nextElementSequence,
  renderSavedReportInfo, renderSavedReportForm,
  onAddReports, onAddById,
  selectCurrent, updateValidation, loadEvaluation, displayError,
  validateSavedReport, updateSavedReport, deleteSavedReport, deleteAllSavedReports,
  validation, translation, tileConfig
}: PropsWithChildren<SavedReportTileProps<SavedReport>>) => {

  const onDrag = (dragSavedReport: SavedReport, sequence: number) => {
    updateSavedReport(
      dragSavedReport?.code?.value,
      { ...dragSavedReport, sequence },
      validation[dragSavedReport?.code?.value]
    );
  };

  const onShowDetails = () => {
    selectCurrent(savedReport, false);
  };

  const onHideDetails = () => {
    deleteSavedReport(undefined);
    selectCurrent(undefined, false);
  };

  const onDone = processSavedReport({
    currentSavedReport, currentSavedReportId, allSavedReportIs,
    validateSavedReport, updateSavedReport, updateValidation, loadEvaluation, displayError, onClose: onHideDetails,
    validation: currentValidation, translation
  });

  const active = !!currentSavedReport;

  const wrapperStyle = `${styles.tileWrapper} ${active ? styles.activeTile : ''}`;

  return <div className="col-lg-6 col-sm-12">
    <SavedReportDrop
      item={savedReport}
      type={SavedReportDragItemType.savedReportTile}
      updateItem={onDrag}>
      {active && <div tabIndex={0} onFocus={onHideDetails} />}
      <div className={wrapperStyle}>
        <ListItem>
          <SavedReportDrag
            item={savedReport}
            type={SavedReportDragItemType.savedReportTile}
            canDrag={tileConfig.move && !active}>
            <ListItemHeader>
              <SavedReportTileHeader
                savedReport={savedReport}
                canDrag={tileConfig.move}
                disabledLabel={translation.disabled} />
              <SavedReportMeatball
                savedReport={savedReport}
                prevElementSequence={prevElementSequence}
                nextElementSequence={nextElementSequence}
                onAddReports={onAddReports}
                onAddById={onAddById}
                updateSavedReport={updateSavedReport}
                deleteSavedReport={deleteSavedReport}
                deleteAllSavedReports={deleteAllSavedReports}
                onHideDetails={onHideDetails}
                validation={validation}
                translation={translation}
                tileConfig={tileConfig} />
            </ListItemHeader>
          </SavedReportDrag>
          {!!renderSavedReportInfo && <ListItemBody>
            <div className={styles.bodyWrapper}>
              {renderSavedReportInfo(savedReport)}
            </div>
          </ListItemBody>}
          <SavedReportDetailsButton
            active={active}
            onShowDetails={onShowDetails}
            onHideDetails={onHideDetails}
            validation={validation[savedReport?.code?.value]}
            translation={translation} />
        </ListItem>
        {active && <SavedReportDetails
          savedReport={currentSavedReport}
          renderSavedReportForm={renderSavedReportForm}
          onHideDetails={onHideDetails}
          onDone={onDone}
          translation={translation} />}
        {active && <ExpanderBackground onClick={onHideDetails} />}
      </div>
    </SavedReportDrop>
  </div>;
};
